<template lang="pug">
  div.p-multi-select(
    translate="no",
    :class="[containerClass, {'display-mode': displayMode}]",
    :style="containerStyle"
  )
    multiselect(
      v-bind="multiselectAttrs"
      v-model="parsedState"
      :options="customOptions"
      :placeholder="forcedPlaceholder ? '' : placeholderSeletor()"
      :forcedLabelUp="Boolean(forcedPlaceholder)"
      multiple
      :track-by="trackBy"
      :label="labelItem"
      :loading="objectLoading || commonLoading"
      :openDirection="openDirection"
      :closeOnSelect="false"
      :disabled="disabled"
      :required="required"
      :error="Boolean(error)"
      name="multiselect"
      @dblclick="dblclick",
      @select="handleSelect"
      ref="multiselect"
      @search-change="changeSearch"
      @load-more="carregarMaisPorScroll"
      @open="seletorAberto = true"
      @close="seletorAberto = false"
    )
      template(v-if="mostrarBotaoCarregarMais" #opcaoEspecial)
        PontotelBotao(peso="pesado"
          @clique="fetchItemsComPaginacao"
          tipo="primario"
          tamanho="P"
        ) {{ $t('compartilhados.selectors.PMultiSelect.carregarMais') }}

      template(#selection="{ values, search, isOpen }")
        template(v-if="!isOpen")
          span.multiselect__single(v-if="!forcedPlaceholder && values.length > 1")
            | {{ values.length }} {{ $t('compartilhados.selectors.PMultiSelect.itens') }} {{ displayMode ? '' : $t('compartilhados.selectors.PMultiSelect.selecionados') }}

          span.multiselect__single(v-else-if="!forcedPlaceholder && values.length === 1")
            | {{ values[0][labelItem] }}

          span.multiselect__single(v-else="")
            | {{ forcedPlaceholder }}

      template(#tag="{ option, remove }")
        span.custom__tag(style="display: none;")

      template(#noResult)
        span.multiselect__no-result
          | {{ $t('compartilhados.selectors.PMultiSelect.nadaEncontrado') }}

      template(#noOptions)
        span.multiselect__no-options
          | {{ $t('compartilhados.selectors.PMultiSelect.nenhumaOpcao') }}

    ErrorMsg(:error="error")

</template>

<script>
import canUseCommonState from 'src/common/mixins/canUseCommonState'
import Multiselect from './_MultiselectWrapperLegado'
import ErrorMsg from 'src/common/inputs/ErrorMsg'
import { includesSearch } from 'src/utils'
import { debounce } from 'lodash'
import { extrairAtributosDeControle, extrairClasseDoContainer, extrairEstiloDoContainer } from 'src/common/mixins/localListenersAndAttributes'

const ID_OPCAO_SELECIONAR_TODOS = '__internal_selectall'
const TEMPO_IGNORAR_DUPLO_CLIQUE_SELECIONAR_TODOS_MS = 500

export default {
  name: 'PMultiSelectLegado',
  inheritAttrs: false,
  emits: ['update:modelValue', 'update:selecionouTodos', 'change', 'select', 'dblclick'],
  mixins: [canUseCommonState()],
  components: {
    Multiselect, ErrorMsg
  },
  props: {
    usaPaginacao: Boolean,
    disabled: Boolean,
    labelItem: { type: String, required: true },
    title: { type: String },
    forcedPlaceholder: { type: String },
    trackBy: { type: String, default: 'id' },
    customLabel: { type: Function },
    loading: { type: Boolean },
    required: { type: Boolean, default: false },
    displayMode: { type: Boolean, default: false },
    openDirection: { type: String, default: 'below' },
    permiteSelecionarTodos: { type: Boolean, default: true },
    // Simetria de contrato com PSelect: quando uma lista externa (`options`) é
    // fornecida, o loading da store não deve acender o spinner (dados já prontos).
    options: { type: Array, default: null },
    error: String,
    tamanhoMaximoCaracteresInseridos: {
      type: Number,
      'default': 200
    }
  },
  data () {
    return {
      ultimoCliqueSelecionarTodos: 0,
      search: '',
      seletorAberto: false
    }
  },
  created () {
    this.debouncedSearch = debounce((query) => {
      this.search = query
      // Busca no servidor só enquanto a lista for paginada e ainda NÃO estiver
      // completa em memória; se já está completa (pré-carga/cache), o customOptions
      // filtra no cliente e não precisamos ir à rede (nem do "carregar mais").
      if (this.usaPaginacao && !this.moduloTemListaCompleta()) {
        this.fetchItemsComPaginacao(false)
      }
    }, 150)
  },
  beforeUnmount () {
    this.debouncedSearch?.cancel()
  },
  computed: {
    containerClass () {
      return extrairClasseDoContainer(this.$attrs)
    },
    containerStyle () {
      return extrairEstiloDoContainer(this.$attrs)
    },
    multiselectAttrs () {
      return extrairAtributosDeControle(this.$attrs)
    },
    mostrarBotaoCarregarMais () {
      const {usaPaginacao} = this
      if (!usaPaginacao) return
      return this.aindaFaltaMostrarPaginas || false
    },
    aindaFaltaMostrarPaginas () {
      let { useCommon } = this
      if (useCommon.includes('/')) {
        useCommon = useCommon.split('/')[0]
      }
      return this.$store.getters[`common/${useCommon}/aindaFaltaMostrarPaginas`]
    },
    customOptions: {
      get () {
        if (this.state.loaded.length === 0) return []

        const source = this.displayMode ? (this.state?.selected || []) : this.state.loaded
        const haveFieldCode = this.state.loaded[0].hasOwnProperty('code')

        const options = source.filter(option => {
          if (includesSearch(option[this.labelItem], this.search)) return true
          if (haveFieldCode) {
            if (option.code && includesSearch(option.code, this.search)) return true
            if (option.phoneclockPassword && includesSearch(option.phoneclockPassword, this.search)) return true
          }
          return false
        })

        if (this.displayMode || !this.permiteSelecionarTodos) {
          return options
        }
        return [
          {[this.labelItem]: this.$t('compartilhados.selectors.PMultiSelect.todos'), id: ID_OPCAO_SELECIONAR_TODOS},
          ...options
        ]
      }
    },
    parsedState: {
      set (value) {
        let valueIncludesSelectAll = false

        const normalizedValue = Array.isArray(value)
          ? value.filter(item => {
            if (!item) return false
            if (item.id === ID_OPCAO_SELECIONAR_TODOS) {
              valueIncludesSelectAll = true
              return false
            }
            return true
          })
          : []

        if (valueIncludesSelectAll) return

        if (!this.displayMode) {
          this.updateSelectedState(normalizedValue, false)
        }
      },
      get () {
        return Array.isArray(this.state.selected)
          ? this.state.selected.filter(Boolean)
          : []
      }
    },
    objectLoading: {
      get () {
        // Espelha PSelect: suprime o loading quando há lista externa (`options`).
        return !this.options && this.state.loading
      }
    }
  },
  methods: {
    placeholderSeletor () {
      if (this.seletorAberto && !this.displayMode) {
        return `${this.title} - ${this.quantidadeSelecionados()}`
      }
      return this.title
    },
    quantidadeSelecionados () {
      if (this.state.selected) {
        const quantidade = this.state.selected.length
        let texto = this.$t('compartilhados.selectors.PMultiSelect.nenhumSelecionado')

        if (quantidade === 1) {
          texto = quantidade + this.$t('compartilhados.selectors.PMultiSelect.quantidadeSelecionada')
        } else if (quantidade > 1) {
          texto = quantidade + this.$t('compartilhados.selectors.PMultiSelect.quantidadeSelecionados')
        }
        return texto
      }
      return '0'
    },
    async fetchItemsComPaginacao (append = true) {
      let {useCommon, search, withFields, withMinimalInformation, withParams} = this
      if (useCommon.includes('/')) {
        useCommon = useCommon.split('/')[0]
      }
      const payload = { append, search, withFields, withMinimalInformation, withParams }
      this.$store.dispatch(`common/${useCommon}/fetchItemsComPaginacao`, payload)
    },
    carregarMaisPorScroll () {
      // Auto-carrega a próxima página ao rolar até perto do fim (substitui o
      // clique em "Carregar mais"). Guardas: só paginado, só se ainda há páginas
      // e se não há fetch em voo (evita disparos repetidos do scroll). O append
      // preserva a busca atual, então funciona também com filtro por nome.
      if (!this.usaPaginacao || !this.useCommon) { return }
      if (!this.aindaFaltaMostrarPaginas) { return }
      const modulo = this.useCommon.split('/')[0]
      if (this.$store.state.common[modulo]?.loading) { return }
      this.fetchItemsComPaginacao(true)
    },
    open () {
      this.$refs.multiselect.activate()
    },
    dblclick (...params) {
      if (params[0].id !== ID_OPCAO_SELECIONAR_TODOS) {
        this.$emit('dblclick', ...params)
      }
    },
    async carregarItensComPaginacao () {
      let {useCommon} = this
      if (useCommon.includes('/')) {
        useCommon = useCommon.split('/')[0]
      }
      // Ao clicar "todos" com paginação, busca a lista completa padrão sem os
      // filtros usados na página atual.
      const payload = { withFields: [], withMinimalInformation: false, withParams: {} }
      await this.$store.dispatch(`common/${useCommon}/fetchItems`, payload)
    },
    async handleSelect (item) {
      this.$emit('select', item)

      if (item?.id === ID_OPCAO_SELECIONAR_TODOS) {
        const agora = Date.now()
        if (agora - this.ultimoCliqueSelecionarTodos < TEMPO_IGNORAR_DUPLO_CLIQUE_SELECIONAR_TODOS_MS) return
        this.ultimoCliqueSelecionarTodos = agora

        if (this.usaPaginacao) {
          await this.carregarItensComPaginacao()
        }

        const opcoesSelecionaveis = this.customOptions.filter(opcao => opcao.id !== ID_OPCAO_SELECIONAR_TODOS)
        const idsSelecionados = new Set(this.parsedState.map(opcao => opcao.id))
        const todasOpcoesSelecionadas = opcoesSelecionaveis.length > 0 && opcoesSelecionaveis.every(opcao => idsSelecionados.has(opcao.id))
        const selecionouTodos = !todasOpcoesSelecionadas && !this.search

        // Informa aos consumidores se a alteração representa a seleção da opção "Todos".
        this.updateSelectedState(todasOpcoesSelecionadas ? [] : opcoesSelecionaveis, selecionouTodos)
      }
    },
    updateSelectedState (selected, selecionouTodos) {
      const newState = { selected }

      this.$emit('update:selecionouTodos', selecionouTodos)

      this.state = newState

      if (this.modelValue === undefined || this.customVmodel) {
        this.$emit('change', newState)
      }
    },
    verificaTamanhoCaracteres () {
      const motivo = this.$refs.multiselect.getSearch()
      if (motivo.length > this.tamanhoMaximoCaracteresInseridos) {
        this.$refs.multiselect.setSearch(motivo.substring(0, this.tamanhoMaximoCaracteresInseridos))
      }
    },
    changeSearch (query) {
      const querySemEspacos = query.trim()

      this.verificaTamanhoCaracteres()
      this.debouncedSearch(querySemEspacos)
    }
  }
}
</script>

<style>
.p-select__error--message {
  display: block;
  font-size: .75rem;
  padding-top: 2px;
  padding-bottom: 2px;
  color: var(--color-danger);
}
.p-select__error--placeholder {
  color: transparent;
  font-size: .75rem;
  padding-top: 2px;
  padding-bottom: 2px;
  display: block;
  -webkit-touch-callout: none;
  -webkit-user-select: none;
  -khtml-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
}
.p-multi-select {
  box-sizing: border-box;
}

.multiselect__single {
  white-space: nowrap;
  text-overflow: ellipsis;
}

.display-mode .multiselect__element {
  cursor: default !important;
}
.display-mode .multiselect__option--selected, .display-mode .multiselect__option--highlight, .display-mode .multiselect__option--highlight:after {
  cursor: default !important;
  color: var(--input-cor-habilitado-conteudo-preenchido) !important;
  background: transparent !important;
}
</style>
